<template>
  <div class="container is-fluid p-4">
    <template v-if="loaded">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <span class="is-size-3 has-text-weight-bold">{{
              $t("mp:dashboard.title")
            }}</span>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item mobile-no-inline-margin">
            <b-select
              v-model="dashboard.queryType"
              placeholder="Select a name"
              class="mobile-full-width"
              expanded
            >
              <option
                v-for="queryType in queryTypes"
                :key="queryType"
                :value="queryType"
              >
                {{ $t(`mp:dashboard.query_type.${queryType.toLowerCase()}`) }}
              </option>
            </b-select>
          </div>
          <div class="level-item mobile-no-inline-margin">
            <b-select
              v-model="dashboard.currency"
              placeholder="Select a name"
              class="mobile-full-width"
              expanded
            >
              <option
                v-for="currency in currencies"
                :key="currency"
                :value="currency"
              >
                {{ currency }}
              </option>
            </b-select>
          </div>
          <div class="level-item mobile-no-inline-margin">
            <b-datepicker
              class="mobile-full-width"
              :placeholder="
                $t('mp:dashboard.datepicker.start_date.placeholder')
              "
              v-model="dashboard.startDate"
              nearby-selectable-month-days
              position="is-bottom-left"
              icon="calendar"
              :max-date="startDateMax"
            >
            </b-datepicker>
            <b-datepicker
              class="mobile-full-width"
              :placeholder="$t('mp:dashboard.datepicker.end_date.placeholder')"
              v-model="dashboard.endDate"
              nearby-selectable-month-days
              position="is-bottom-left"
              icon="calendar"
              :min-date="endDateMin"
              :max-date="new Date()"
            >
            </b-datepicker>
          </div>
        </div>
      </div>
      <section>
        <div class="level">
          <div
            v-for="dataset in chartData.datasets"
            :key="dataset.name"
            class="level-item card is-flex-1"
          >
            <div class="is-flex is-flex-direction-column expand p-5">
              <span class="is-size-6 is-grey">{{
                $t(`mp:dashboard.aggregated.name.${dataset.name.toLowerCase()}`)
              }}</span>
              <span
                v-if="dataset.dataType === 'PERCENT'"
                class="is-size-3 has-text-weight-bold"
              >
                {{ formatPercent(dataset.aggregated) }}
              </span>
              <span
                v-else-if="dataset.dataType === 'AMOUNT'"
                class="is-size-3 has-text-weight-bold"
              >
                {{ formatAmount(dataset.aggregated) }}
              </span>
              <span v-else class="is-size-3 has-text-weight-bold">
                {{ formatNumber(dataset.aggregated) }}
              </span>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-content">
            <div class="is-flex is-flex-direction-column">
              <div class="chart-header">
                <div class="is-flex is-flex-direction-column">
                  <span class="is-size-5 has-text-weight-bold mb-2">{{
                    $t("mp:dashboard.chart.title")
                  }}</span>
                  <span class="is-size-7 is-grey">{{
                    $t("mp:dashboard.chart.subtitle")
                  }}</span>
                </div>
                <div class="chart-button-groups">
                  <div class="buttons has-addons m-0">
                    <button
                      v-for="valueType in DASHBOARD_VALUE_TYPE"
                      :key="valueType"
                      class="button is-small is-blue m-0"
                      :class="{
                        'is-active': dashboard.valueType === valueType,
                      }"
                      @click="setValueType(valueType)"
                    >
                      {{
                        $t(`mp:dashboard.value_type.${valueType.toLowerCase()}`)
                      }}
                    </button>
                  </div>
                  <div class="buttons has-addons m-0">
                    <template
                      v-for="groupByPeriod in DASHBOARD_GROUP_BY_PERIODS"
                    >
                      <button
                        :key="groupByPeriod"
                        v-if="availableGroupByPeriods[groupByPeriod]"
                        class="button is-small is-blue m-0"
                        :class="{
                          'is-active':
                            dashboard.groupByPeriod === groupByPeriod,
                        }"
                        @click="setGroupByPeriod(groupByPeriod)"
                      >
                        {{
                          $t(
                            `mp:dashboard.group_by_period.${groupByPeriod.toLowerCase()}`
                          )
                        }}
                      </button>
                    </template>
                  </div>
                </div>
              </div>
              <Bar
                :chartOptions="chartOptions"
                :chartData="chartData"
                style="width: 100%; height: 400px; position: relative"
              ></Bar>
            </div>
          </div>
        </div>
      </section>
      <p class="is-size-5 has-text-weight-bold mt-5">
        {{ $t("mp:dashboard.section.title.details") }}
      </p>
      <section>
        <b-table
          :data="range(chartData.datasets[0].data.length)"
          :paginated="true"
        >
          <b-table-column
            :label="$t('mp:dashboard.table.header.date')"
            v-slot="props"
          >
            {{ formatDate(new Date(chartData.labels[props.row])) }}
          </b-table-column>

          <b-table-column
            v-for="column in chartData.datasets"
            :key="column.name"
            :label="
              $t(`mp:dashboard.table.header.${column.name.toLowerCase()}`)
            "
            v-slot="props"
          >
            <template v-if="column.dataType === 'PERCENT'">
              {{ formatPercent(column.data[props.row]) }}
            </template>
            <template v-else-if="column.dataType === 'AMOUNT'">
              {{ formatAmount(column.data[props.row]) }}
            </template>
            <template v-else-if="column.dataType === 'NUMBER'">
              {{ formatNumber(column.data[props.row]) }}
            </template>
            <template v-else>
              {{ column.data[props.row] }}
            </template>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              {{ $t("mp:dashboard.details_table.empty") }}
            </div>
          </template>
        </b-table>
      </section>
      <PreScoreConversionCard />
    </template>
    <LoadingSkeleton v-else />
  </div>
</template>

<script>
import { range } from "lodash";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import { mapActions, mapGetters } from "vuex";
import {
  DASHBOARD_GROUP_BY_PERIODS,
  DASHBOARD_VALUE_TYPE,
} from "@/utils/const";
import "chart.js/auto";
import { Bar } from "vue-chartjs";

const chartColors = ["#1072FC", "#02EC88", "#FC9D45"];

export default {
  name: "Dashboard",
  components: {
    LoadingSkeleton,
    Bar,
    PreScoreConversionCard: () =>
      import("@/components/bnpl/PreScoreConversionCard.vue"),
  },
  data() {
    return {
      DASHBOARD_GROUP_BY_PERIODS,
      DASHBOARD_VALUE_TYPE,
      loaded: false,
      locale: "hu-HU",
      chartData: {
        labels: [],
        datasets: [],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
          },
          tooltip: {
            callbacks: {
              title: (context) => {
                return this.formatDate(new Date(context[0].label));
              },
            },
          },
        },
        interaction: {
          mode: "index",
        },
        scales: {
          percentAxes: {
            type: "linear",
            position: "right",
            display: "auto",
            min: 0,
            max: 1,
            ticks: {
              count: 11,
              callback: (value) => {
                return this.formatPercent(value);
              },
            },
            grid: {
              drawOnChartArea: false,
            },
          },
          numberAxes: {
            type: "linear",
            position: "left",
            display: "auto",
            ticks: {
              callback: (value) => {
                if (value % 1 === 0) {
                  return this.formatNumber(value);
                }
                return "";
              },
            },
          },
          amountAxes: {
            type: "linear",
            position: "left",
            display: "auto",
            ticks: {
              callback: (value) => {
                return this.formatAmount(value);
              },
            },
            tooltip: {
              callback: (value) => {
                return this.formatAmount(value);
              },
            },
          },
          x: {
            ticks: {
              callback: (value, index) => {
                return this.formatDate(new Date(this.chartData.labels[index]));
              },
            },
          },
        },
      },
      dashboard: {
        queryType: null,
        startDate: null,
        endDate: null,
        groupByPeriod: null,
        valueType: null,
        currency: null,
      },
      startDateMax: null,
      endDateMin: null,
      availableGroupByPeriods: {},
    };
  },
  computed: {
    ...mapGetters({
      queryTypes: "dashboard/getQueryTypes",
      chart: "dashboard/getChart",
      country: "i18n/country",
      currencies: "i18n/currencies",
    }),
  },
  methods: {
    ...mapActions({
      fetchQueryTypes: "dashboard/fetchQueryTypes",
      fetchChart: "dashboard/fetchChart",
    }),
    range,
    setGroupByPeriod(value) {
      this.dashboard.groupByPeriod = value;
    },
    setValueType(value) {
      this.dashboard.valueType = value;
    },
    mapChart() {
      const chart = this.chart;
      this.chartData.labels = chart.labels;
      this.chartData.datasets = chart.datasets.map((dataset, index) => {
        dataset.type = dataset.type.toLowerCase();
        dataset.backgroundColor = chartColors[index];
        dataset.borderColor = chartColors[index];

        if (dataset.dataType === "PERCENT") {
          dataset.order = -1;
          dataset.yAxisID = "percentAxes";
        } else if (dataset.dataType === "NUMBER") {
          dataset.yAxisID = "numberAxes";
        } else if (dataset.dataType === "AMOUNT") {
          dataset.yAxisID = "amountAxes";
        }

        if (dataset.type === "line") {
          dataset.pointRadius = 0;
          dataset.pointHitRadius = 10;
        }

        dataset.label = this.$i18n.t(
          `mp:dashboard.legend.name.${dataset.name.toLowerCase()}`
        );

        dataset.tooltip = {
          callbacks: {
            label: (context) => {
              if (dataset.dataType === "PERCENT") {
                return `${this.$i18n.t(
                  `mp:dashboard.tooltip.name.${dataset.name.toLowerCase()}`
                )}: ${this.formatPercent(context.parsed.y)}`;
              } else if (dataset.dataType === "NUMBER") {
                return `${this.$i18n.t(
                  `mp:dashboard.tooltip.name.${dataset.name.toLowerCase()}`
                )}: ${this.formatNumber(context.parsed.y)}`;
              } else if (dataset.dataType === "AMOUNT") {
                return `${this.$i18n.t(
                  `mp:dashboard.tooltip.name.${dataset.name.toLowerCase()}`
                )}: ${this.formatAmount(context.parsed.y)}`;
              }
              return `${this.$i18n.t(
                `mp:dashboard.tooltip.name.${dataset.name.toLowerCase()}`
              )}: ${context.parsed.y}`;
            },
          },
        };
        return dataset;
      });
    },
    formatPercent(value) {
      const formatter = new Intl.NumberFormat(this.country.languageIso, {
        style: "percent",
        roundingPriority: "lessPrecision",
      });
      return formatter.format(value);
    },
    formatNumber(value) {
      const formatter = new Intl.NumberFormat(this.country.languageIso);
      return formatter.format(value);
    },
    formatAmount(value) {
      const formatter = new Intl.NumberFormat(this.country.languageIso, {
        style: "currency",
        currency: this.dashboard.currency,
        trailingZeroDisplay: "stripIfInteger",
      });
      return formatter.format(value);
    },
    formatDate(value) {
      if (this.dashboard.groupByPeriod === DASHBOARD_GROUP_BY_PERIODS.YEARLY) {
        return new Intl.DateTimeFormat(this.country.languageIso, {
          year: "numeric",
        }).format(value);
      } else if (
        this.dashboard.groupByPeriod === DASHBOARD_GROUP_BY_PERIODS.MONTHLY
      ) {
        return new Intl.DateTimeFormat(this.country.languageIso, {
          year: "numeric",
          month: "2-digit",
        }).format(value);
      } else if (
        this.dashboard.groupByPeriod === DASHBOARD_GROUP_BY_PERIODS.WEEKLY
      ) {
        return new Intl.DateTimeFormat(this.country.languageIso, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(value);
      } else if (
        this.dashboard.groupByPeriod === DASHBOARD_GROUP_BY_PERIODS.DAILY
      ) {
        return new Intl.DateTimeFormat(this.country.languageIso, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(value);
      }
      return value;
    },
    calculateMinMaxDates() {
      this.startDateMax = this.dashboard.endDate;
      this.endDateMin = this.dashboard.startDate;
    },
    calculateAvailableGroupByPeriods() {
      const days = Math.round(
        (this.dashboard.endDate - this.dashboard.startDate) /
          (24 * 60 * 60 * 1000)
      );
      const weeks = Math.round(
        (this.dashboard.endDate - this.dashboard.startDate) /
          (24 * 60 * 60 * 1000 * 7)
      );
      const months = Math.round(
        (this.dashboard.endDate - this.dashboard.startDate) /
          (24 * 60 * 60 * 1000 * 30)
      );
      this.availableGroupByPeriods = {
        YEARLY: true,
        MONTHLY: months <= 90,
        WEEKLY: weeks <= 90,
        DAILY: days <= 90,
      };
    },
    smallestAvailableGroupByPeriod() {
      if (!this.availableGroupByPeriods.DAILY) {
        if (!this.availableGroupByPeriods.WEEKLY) {
          if (!this.availableGroupByPeriods.MONTHLY) {
            return DASHBOARD_GROUP_BY_PERIODS.YEARLY;
          }
          return DASHBOARD_GROUP_BY_PERIODS.MONTHLY;
        }
        return DASHBOARD_GROUP_BY_PERIODS.WEEKLY;
      }
      return DASHBOARD_GROUP_BY_PERIODS.DAILY;
    },
  },
  watch: {
    dashboard: {
      async handler() {
        this.loaded = false;
        this.calculateMinMaxDates();
        this.calculateAvailableGroupByPeriods();
        if (!this.availableGroupByPeriods[this.dashboard.groupByPeriod]) {
          this.dashboard.groupByPeriod = this.smallestAvailableGroupByPeriod();
        }
        localStorage.setItem(
          "mp-dashboard-query",
          JSON.stringify(this.dashboard)
        );
        await this.fetchChart(this.dashboard);
        this.mapChart();
        this.loaded = true;
      },
      deep: true,
    },
    country: {
      async handler() {
        if (this.loaded) {
          this.mapChart();
        }
      },
    },
  },
  async created() {
    await this.fetchQueryTypes();

    const savedDashboardQuery = JSON.parse(
      localStorage.getItem("mp-dashboard-query")
    );
    if (savedDashboardQuery) {
      savedDashboardQuery.startDate = new Date(savedDashboardQuery.startDate);
      savedDashboardQuery.endDate = new Date(savedDashboardQuery.endDate);
      this.dashboard = savedDashboardQuery;
    } else {
      let today = new Date();
      this.dashboard.queryType = this.queryTypes[0];
      this.dashboard.endDate = today;
      this.dashboard.startDate = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        today.getDate()
      );
      this.dashboard.groupByPeriod = DASHBOARD_GROUP_BY_PERIODS.DAILY;
      this.dashboard.valueType = DASHBOARD_VALUE_TYPE.VALUE;
      this.dashboard.currency = this.country.currencyIso;

      localStorage.setItem(
        "mp-dashboard-query",
        JSON.stringify(this.dashboard)
      );
    }
  },
};
</script>

<style scoped lang="scss">
.card {
  border-radius: 8px;
  box-shadow: none;
  border: 1px solid #e0e5eb;
}

.button.is-blue.is-active {
  background-color: #0077ff80;
}

.button.is-small:not(.is-rounded) {
  border-radius: 6px;
}

.buttons.has-addons .button:not(:first-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.buttons.has-addons .button:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.is-grey {
  color: #677d99;
}

.expand {
  flex: 1;
}

.level {
  gap: 18px;
  margin-bottom: 18px;
  flex-wrap: wrap;

  .level-right {
    justify-content: flex-start;
    flex-wrap: wrap;
    max-width: 100%;
  }

  .level-item {
    margin-bottom: 18px;
    gap: 18px;
  }
}

.is-flex-1 {
  flex: 1;
}

.level-right .level-item:not(:last-child) {
  margin-right: 18px;
}

section {
  margin-top: 18px;
}

.chart-button-groups {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

@media only screen and (max-width: 769px) {
  .mobile-full-width {
    flex: 1;
  }

  .chart-button-groups {
    flex-direction: column;
    align-items: start;
    margin-top: 16px;
  }

  .level-item {
    flex-direction: column;
    align-items: stretch;
  }

  .mobile-no-inline-margin {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .mobile-block-margin {
    margin-block: 12px;
  }
}
</style>

<style lang="scss">
.b-table .table {
  border-radius: 8px;
}

.pagination-list {
  flex-wrap: nowrap;
}
</style>
